/**
 * lib-components
 *
 * Adapptio library to help creating components.
 *
 * @package lib-components
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import { createContext, useContext } from "react";

import { ItemRegistry } from "@adapptio/lib-shared";
import { RuntimeContext, RUNTIME_CONTEXT_MODE } from "@adapptio/lib-blueprint";

import { TGenericViewComponentDefinition } from "./TViewComponentDefinition";
import { IViewport } from "../Classes/Viewport";
import { IToastMessageManager } from "../ui/activities/ToastMessageManager";
import { IToastMessage, IToastMessageActivityItem } from "../ui/activities/ToastMessage";

/**
 *  Components render mode
 */
export enum RENDER_MODE {
	BROWSER = "browser",
	SSR = "ssr"
}

export interface IComponentMainContextValue {
	componentRegistry: ItemRegistry<TGenericViewComponentDefinition>;
	rCtx: RuntimeContext;
	mode: RUNTIME_CONTEXT_MODE;
	renderMode: RENDER_MODE;
	wasRenderedWithSsr: boolean;
	viewport: IViewport;
	toastMessageManager: IToastMessageManager<IToastMessageActivityItem, IToastMessage>;
	appRootId: string;
}

export interface IComponentChildContextValue {
	dndState: {
		active: boolean;
	};
}

/**
 *  Component Main Context
 */
export const ComponentMainContext = createContext<IComponentMainContextValue>(null);

export function useComponentMainContext(): IComponentMainContextValue {
	return useContext(ComponentMainContext);
}

/**
 *  Component Child Context (passed to component children)
 */
export const ComponentChildContext = createContext<IComponentChildContextValue>(null);
